import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { Close, InfoOutlined } from "@mui/icons-material";
import { convertToINR } from "../../../helpers/Utils";
import { TOKENS } from "../constants/figmaTokens";

function FieldRow({ label, prefix, value, valueBold, compact = false }) {
  return (
    <Box display="flex" alignItems="center" gap={compact ? "12px" : "24px"} px={compact ? "16px" : "24px"}>
      <Typography
        sx={{
          width: compact ? 120 : 200,
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "20px",
          color: TOKENS.onSurfaceSecondary,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          gap: "8px",
          alignItems: "baseline",
          justifyContent: "flex-end",
        }}
      >
        {prefix && (
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: "16px",
              color: TOKENS.onSurfaceSecondary,
            }}
          >
            {prefix}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: valueBold ? 500 : 400,
            lineHeight: "20px",
            color: TOKENS.onSurfaceDefault,
            textAlign: "right",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

export default function CreditNoteRequiredDialog({
  open,
  onCancel,
  onContinue,
  revisedPOAmount,
  currentPOAmount,
  invoiceCount = 0,
  approvedInvoiceAmount = 0,
  paymentCount = 0,
  amountPaid = 0,
  isMobile = false,
}) {
  const creditNoteAmount = Math.max(0, (approvedInvoiceAmount || 0) - (revisedPOAmount || 0));

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? "92vw" : 600,
          maxWidth: isMobile ? "92vw" : undefined,
          borderRadius: "4px",
          margin: 0,
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(0, 0, 0, 0.4)" },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "16px",
          p: isMobile ? "16px" : "24px",
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontSize: 20,
            fontWeight: 700,
            lineHeight: "24px",
            color: TOKENS.onSurfaceDefault,
          }}
        >
          Accept revision
        </Typography>
        <IconButton onClick={onCancel} size="small" sx={{ p: 0 }}>
          <Close sx={{ fontSize: 24, color: TOKENS.onSurfaceSecondary }} />
        </IconButton>
      </Box>

      {/* Body rows */}
      <Box display="flex" flexDirection="column" gap="16px" width="100%">
        <FieldRow
          label="Revised PO amount"
          value={revisedPOAmount != null ? convertToINR(revisedPOAmount) : "-"}
          valueBold
          compact={isMobile}
        />
        <FieldRow
          label="Current PO amount"
          value={currentPOAmount != null ? convertToINR(currentPOAmount) : "-"}
          compact={isMobile}
        />
        <FieldRow
          label="Approved invoice amount"
          compact={isMobile}
          prefix={
            invoiceCount > 0
              ? `(${invoiceCount} Invoice${invoiceCount > 1 ? "s" : ""})`
              : null
          }
          value={convertToINR(approvedInvoiceAmount || 0)}
        />
        <FieldRow
          label="Amount paid"
          compact={isMobile}
          prefix={
            paymentCount > 0
              ? `(${paymentCount} Payment${paymentCount > 1 ? "s" : ""})`
              : null
          }
          value={convertToINR(amountPaid || 0)}
        />

        {/* Credit note required block — light lavender */}
        <Box px={isMobile ? "16px" : "24px"}>
          <Box
            sx={{
              bgcolor: TOKENS.tertiaryBlueVariant2,
              border: `1px solid ${TOKENS.tertiaryBlueVariant1}`,
              borderRadius: "8px",
              p: "12px 16px",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault }}
              >
                Credit note required
              </Typography>
              <Typography
                sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
              >
                (Approved invoice amount - Revised PO amount)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: "24px",
                  color: TOKENS.onTertiaryBlueVariant,
                }}
              >
                {convertToINR(creditNoteAmount)}
              </Typography>
              <Typography
                sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceDefault }}
              >
                Credit note amount
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Why is credit note required? — light gray */}
        <Box px={isMobile ? "16px" : "24px"}>
          <Box
            sx={{
              bgcolor: TOKENS.surfaceBg,
              border: `1px solid ${TOKENS.onSurfaceBorder}`,
              borderRadius: "4px",
              p: "12px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Box display="flex" alignItems="center" gap="8px">
              <InfoOutlined sx={{ fontSize: 20, color: TOKENS.onSurfaceSecondary }} />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: "20px",
                  color: TOKENS.onSurfaceDefault,
                }}
              >
                Why is credit note required?
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault }}
            >
              The PO has been revised. Since the value of approved invoices is more than the
              revised PO amount, you are required to issue a credit note for the difference.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "row",
          gap: "8px",
          justifyContent: "flex-end",
          p: isMobile ? "16px" : "24px",
        }}
      >
        <Button
          variant="contained"
          fullWidth={isMobile}
          onClick={onCancel}
          sx={{
            bgcolor: TOKENS.surfaceVariant,
            color: TOKENS.onSurfaceSecondary,
            boxShadow: "none",
            height: 36,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: TOKENS.surfaceVariant, boxShadow: "none" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          fullWidth={isMobile}
          onClick={onContinue}
          sx={{
            bgcolor: TOKENS.primaryDefault,
            color: "#FFFFFF",
            boxShadow: "none",
            height: 36,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: TOKENS.primaryDefault, boxShadow: "none" },
          }}
        >
          Continue to Credit note
        </Button>
      </Box>
    </Dialog>
  );
}
